import { makeAutoObservable } from 'mobx';
import { token } from '@/infrastructure/container';
import { backoffMs, MAX_ATTEMPTS } from '@/domain/backoff';
import type { IAccessGateway } from '@/domain/accessGateway';
import {
  ConflictError,
  DomainError,
  ForbiddenError,
  NotFoundError,
  ServerError,
} from '@/domain/errors';
import type { JoinParams, ResumeParams } from '@/domain/joinLink';
import { shareForHash, type Role } from '@/domain/project';
import type { Court } from '@/domain/session';
import type { IHasher } from '@/services/Hasher';
import type { IIdGen } from '@/services/IdGen';
import type { ILogger } from '@/services/Logger';
import type { ISleeper } from '@/services/Sleeper';

// The onboarding ViewModel: everything that happens between scanning a QR and having a screen.
// The device redeems the project viewer key, opens the project, hashes the session share key and
// looks the hash up in the registry. What comes back IS the identity: session, role, and court
// where the role has one. Never the URL, never a picker (docs/access.md).
//
// The order below is not a style choice. Opening an online handle subscribes to realtime
// immediately and the realtime endpoint takes no share parameter, so an ungranted client is
// refused: redeem first, open second, on BOTH aggregates (docs/access.md, "Redemption ordering").

export type JoinPhase = 'idle' | 'joining' | 'joined' | 'failed';

// Re-exported so a caller that holds this store does not also have to know which domain module
// parsed the URL it came off.
export type { JoinParams };

export class OnboardingStore {
  private _phase: JoinPhase = 'idle';
  private _role: Role | undefined;
  private _court: Court | undefined;
  private _sessionId: string | undefined;
  private _codeHash: string | undefined;
  private _lastError: string | undefined;

  constructor(
    private readonly gateway: IAccessGateway,
    private readonly hasher: IHasher,
    private readonly logger: ILogger,
    // Appended and optional, the same shape TickEngine and AdminShellStore use, so the
    // three-argument construction the P2 oracle pins still compiles. With no sleeper the ladder
    // retries immediately, which is what a test wants; production passes the real one, because a
    // hall that scans together has to be spread out rather than merely counted.
    private readonly sleeper?: ISleeper,
    // The other half of that. A fresh id per redemption is what makes two phones holding the SAME
    // code wait DIFFERENT amounts; `Math.random` is banned here, so the decorrelation has to come
    // from somewhere and a UUID is where every other ladder in this app gets it.
    private readonly idGen?: IIdGen,
  ) {
    makeAutoObservable(this);
  }

  get phase(): JoinPhase {
    return this._phase;
  }

  get role(): Role | undefined {
    return this._role;
  }

  // Set for a coach, undefined for everyone else.
  get court(): Court | undefined {
    return this._court;
  }

  get sessionId(): string | undefined {
    return this._sessionId;
  }

  // The registry hash of the code this device joined with, set on success only. The shell writes it
  // into the address bar (`resumePath`) so a reload can find its way back; it is the registry's own
  // lookup key, not a secret — holding it without this identity's grants buys nothing.
  get codeHash(): string | undefined {
    return this._codeHash;
  }

  // What the join screen shows when the scan did not work. Never the bearer key: this string is on
  // a phone in a sports hall and the key behind it is a secret.
  get lastError(): string | undefined {
    return this._lastError;
  }

  join = async (params: JoinParams): Promise<void> => {
    // A refusal, not a queue. A second scan while the first is still going would race two identities
    // into the same store and land the device on whichever finished last. The refusal is scoped to a
    // join that is STILL RUNNING: a failed join leaves the store usable, or a blinked wifi means the
    // human is told to reload the page.
    if (this._phase === 'joining') {
      throw new ServerError('Already joining: wait for the current scan to finish.');
    }
    this.startJoin();
    try {
      await this.redeem('the day key', `project:${params.projectId}`, () =>
        this.gateway.redeemProjectShare(params.projectId, params.pk),
      );
      await this.gateway.openProject(params.projectId);
      // One hash, of the session key alone. No salt: the registry is a lookup table the admin
      // device wrote with the same digest, and a salt only either device knows makes the two
      // disagree.
      const codeHash = await this.hasher.sha256Hex(params.sk);
      const share = shareForHash(this.gateway.projectState, codeHash);
      if (share === undefined) {
        // Unknown or disabled, and deliberately the same answer for both: a device holding this
        // morning's re-minted code learns nothing from being told which of the two it is. The
        // message names no key — `what` is a description, not the code.
        throw new NotFoundError('a role for this code');
      }
      // The session comes from the SHARE, never from the URL and never from "the first session on
      // the day". A coach scanning the 11am gold code lands in the 11am session even when the
      // registry lists three others first.
      await this.redeem('the session key', `session:${share.sessionId}:${codeHash}`, () =>
        this.gateway.redeemSessionShare(share.sessionId, params.sk),
      );
      await this.gateway.openSession(share.sessionId);
      this.finishJoin(share.role, share.court, share.sessionId, codeHash);
    } catch (cause) {
      // Translate before anything else looks at it: the gateway is a boundary over foreign code and
      // a bug inside it arrives as a TypeError (INVARIANTS rule 17).
      const failure =
        cause instanceof DomainError ? cause : new ServerError('Could not join.', { cause });
      this.logger.warn('onboarding: join failed', failure.tag);
      this.failJoin(failure);
      // Re-thrown on purpose, unlike a store method bound to a button: the shell awaits this and
      // decides what to render (INVARIANTS rule 17, the "caller that can handle it" carve-out).
      throw failure;
    }
  };

  // A reload, not a scan: the join already happened on this device and the grants it redeemed are
  // persistent for this identity (docs/access.md), so nothing is redeemed here. Open the project,
  // look the stored hash up in the registry — which is where the role STILL comes from, so a code
  // the admin disabled since is refused on the reload exactly as it would be on a re-scan — and
  // open the session the share names.
  //
  // On a device that never joined, `openProject` is refused by the gateway (no grant), which is the
  // whole security argument for carrying the hash in the URL: it resumes a grant, it cannot mint
  // one.
  resume = async (params: ResumeParams): Promise<void> => {
    if (this._phase === 'joining') {
      throw new ServerError('Already joining: wait for the current scan to finish.');
    }
    this.startJoin();
    try {
      await this.gateway.openProject(params.projectId);
      const share = shareForHash(this.gateway.projectState, params.codeHash);
      if (share === undefined) {
        // Unknown or disabled since the join, same answer as the join gives (docs/access.md).
        throw new NotFoundError('a role for this code');
      }
      await this.gateway.openSession(share.sessionId);
      this.finishJoin(share.role, share.court, share.sessionId, params.codeHash);
    } catch (cause) {
      const failure =
        cause instanceof DomainError ? cause : new ServerError('Could not rejoin.', { cause });
      this.logger.warn('onboarding: resume failed', failure.tag);
      this.failJoin(failure);
      // Re-thrown for the same reason join re-throws: the shell awaits this one too.
      throw failure;
    }
  };

  // The door's OCC ladder, around a redemption and nothing else.
  //
  // Redeeming is a WRITE: the gateway appends a grant to the project's access table, conditional on
  // the version it read. Six coaches scanning the same QR off the TV in the same few seconds is the
  // ordinary case, not the pathological one, and a probe of it found phones turned away with a
  // generic "check the wifi" for a code that was perfectly valid. Retrying is safe rather than
  // hopeful: the gateway reserves 403 for a genuine denial, short-circuits a principal that already
  // holds a grant to Granted, and the codes are multi-use by design (docs/access.md), so a second
  // presentation of the same code either lands or is refused for a reason a retry cannot change.
  //
  // Same band and same attempt count as the front desk's and the admin's ladders, from
  // `domain/backoff.ts`, because there is one window docs/sync.md pins and three copies of it would
  // be three chances to drift.
  //
  // ConflictError ONLY. ForbiddenError and NotFoundError are terminal: the code is revoked, burned,
  // or was never minted, and a second attempt fails identically while the person stands in front of
  // the TV waiting for it.
  //
  // The seed is a FRESH id per redemption, not the aggregate it is aimed at, and that is the whole
  // point of the jitter. Six phones holding the same code and hitting the same aggregate would
  // otherwise compute the same delay from the same string and collide again on every rung; measured
  // exactly that way in `load/concurrentJoin.review.load.ts` before the id went in. A store built
  // without an id generator falls back to the aggregate, which is fine for one device and is what
  // the three-argument oracle construction gets.
  private redeem = async (
    what: string,
    fallback: string,
    run: () => Promise<void>,
  ): Promise<void> => {
    const seed = this.idGen?.next() ?? fallback;
    for (let attempt = 1; ; attempt++) {
      try {
        await run();
        return;
      } catch (cause) {
        // Translate first, then decide. A bug inside the gateway arrives as a TypeError and must
        // not be absorbed as if it were a clash on the grant table (INVARIANTS rule 17).
        const failure =
          cause instanceof DomainError
            ? cause
            : new ServerError(`Could not redeem ${what}.`, { cause });
        if (!(failure instanceof ConflictError) || attempt >= MAX_ATTEMPTS) {
          this.logger.warn(
            `onboarding: redeeming ${what} failed on attempt ${attempt}`,
            failure.tag,
          );
          throw failure;
        }
        this.logger.debug(`onboarding: clash redeeming ${what}, retrying (attempt ${attempt})`);
        if (this.sleeper !== undefined) await this.sleeper.sleep(backoffMs(seed, attempt));
      }
    }
  };

  // Actions. makeAutoObservable has wrapped every method, so nothing here needs runInAction.
  private startJoin(): void {
    this._phase = 'joining';
    this._lastError = undefined;
    this.clearIdentity();
  }

  private finishJoin(
    role: Role,
    court: Court | undefined,
    sessionId: string,
    codeHash: string,
  ): void {
    this._role = role;
    this._court = court;
    this._sessionId = sessionId;
    this._codeHash = codeHash;
    this._phase = 'joined';
  }

  // No partial identity survives a failure. A store left holding a role but no session would render
  // a coach screen bound to nothing.
  private failJoin(failure: DomainError): void {
    this.clearIdentity();
    this._phase = 'failed';
    this._lastError = joinMessage(failure);
  }

  private clearIdentity(): void {
    this._role = undefined;
    this._court = undefined;
    this._sessionId = undefined;
    this._codeHash = undefined;
  }
}

// What the person holding the phone is told. Every message ends in the next thing to try, because
// the alternative is a human standing in front of a TV re-scanning the same code.
function joinMessage(failure: DomainError): string {
  // NotFoundError is a code the registry does not know. ForbiddenError is one the GATEWAY refused
  // the redemption for: revoked, a burned single-use code, or one minted for a day that is over.
  // Deliberately the same sentence for both. The difference is not one a person holding a phone
  // can act on differently, and it is not one to hand an attacker probing which codes exist. What
  // matters is that neither says "check the wifi": the wifi is fine, and re-scanning THIS code
  // will fail every time, so the advice has to be a different code.
  if (failure instanceof NotFoundError || failure instanceof ForbiddenError) {
    return 'That code is not valid for today. Scan the code on the TV again.';
  }
  // The ladder ran out against sustained contention on the grant table: the whole hall scanned the
  // same QR at once. The code is fine and so is the wifi, so the advice is to wait a beat rather
  // than to go looking at the router or at a different code.
  if (failure instanceof ConflictError) {
    return 'The hall is busy right now. Scan the code again in a moment.';
  }
  return 'Could not join. Check the wifi and scan again.';
}

export const TOKEN_OnboardingStore = token<OnboardingStore>('OnboardingStore');
